import Vue from 'vue'

/**
 * Pull a human-readable message out of an axios error.
 *
 * Reading `error.response.data.message` directly threw inside the catch block
 * in two situations that happen constantly in production:
 *
 *  - Network error, timeout, CORS rejection or an aborted request leaves
 *    `error.response` undefined, so `.data` threw. The throw escaped the catch
 *    as an unhandled rejection and the user saw NOTHING - the save simply
 *    appeared to do nothing.
 *  - NestJS returns `message` as a plain string for HttpExceptions
 *    (NotFoundException, ConflictException, 500) and only as an array for
 *    ValidationPipe failures. There are 362 string-message throw sites in
 *    routr-backend, so `.join('\n')` threw the same way on the common path.
 *
 * The `devices` store already handled both cases correctly; this generalises
 * that logic so every store shares it.
 */
export function getApiErrorMessage(
  error,
  fallback = 'Something went wrong. Please try again.',
) {
  const data = error && error.response && error.response.data
  const raw = data && (data.message || data.error)

  if (Array.isArray(raw)) {
    const lines = raw.filter(Boolean)
    if (lines.length) return lines.join('\n')
  } else if (typeof raw === 'string' && raw.trim()) {
    return raw
  }

  // A response came back but carried no usable message.
  if (error && error.response) return fallback

  // No response at all: offline, DNS failure, CORS, timeout, or aborted.
  return 'Cannot reach the server. Check your connection and try again.'
}

export const SERVER_UNAVAILABLE_MESSAGE =
  'The server is temporarily unavailable. Please try again in a moment.'

const DEFAULT_FAILURE_FALLBACK = 'Something went wrong. Please try again.'

/**
 * A failure the user can simply retry: the API process is restarting (the
 * dev API gets killed under memory pressure and comes back on its own) or the
 * proxy in front of it answered for it. Those proxy pages are HTML, so the
 * JSON `message` lookup above finds nothing and would otherwise show a vague
 * "Something went wrong" with no hint that waiting a moment fixes it.
 */
export function isTransientApiError(error) {
  if (!error || !error.isAxiosError) return false
  // A cancelled request is the caller's doing, not an outage.
  if (error.code === 'ERR_CANCELED') return false
  if (!error.response) return true
  const status = error.response.status
  return status === 502 || status === 503 || status === 504
}

/**
 * Build the text a toast should show for a failed API call, or `null` when
 * nothing should be shown.
 *
 * The null contract exists so one failure produces exactly one toast: the axios
 * interceptor already toasts 401 ("Session has been expired") and 403
 * ("Permission Denied"), and a cancelled request is never an error to the user.
 * Every other response carries the server's own sentence plus the HTTP status,
 * so a support request can be matched to the backend log line.
 */
export function getApiFailureMessage(
  error,
  fallback = DEFAULT_FAILURE_FALLBACK,
) {
  // A TypeError thrown by our own code should not leak its text to the user.
  if (!error || !error.isAxiosError) return fallback
  if (error.code === 'ERR_CANCELED') return null
  if (isTransientApiError(error)) return SERVER_UNAVAILABLE_MESSAGE

  const status = error.response.status
  if (status === 401 || status === 403) return null

  return `${getApiErrorMessage(error, '') || fallback} (HTTP ${status})`
}

/**
 * Store-side helper: log the raw error for support and toast the user-facing
 * reason through the shared notification mutation. Skips the toast when the
 * failure was already surfaced elsewhere (see getApiFailureMessage).
 */
export function notifyApiFailure(commit, title, error) {
  console.error(error)
  const description = getApiFailureMessage(error)
  if (!description) return
  commit(
    'notification/NEW',
    { type: 'error', message: title, description },
    { root: true },
  )
}

/**
 * Component-side twin of notifyApiFailure: toast the reason unless the axios
 * interceptor already did (401/403 and cancelled requests come back as null),
 * so one failure yields one toast. `fallback` fills in when the server sent
 * no usable message.
 */
export function toastApiFailure(title, error, fallback = 'Please try again.') {
  console.error(error)
  const reason = getApiFailureMessage(error, fallback)
  if (reason === null) return
  Vue.prototype.$notification.error({
    message: title,
    description: reason || fallback,
  })
}
